import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { ALL_PRODUCT_NAMES, CONTACT } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState({ name: "", phone: "", product: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = "Contact Us — Mamta Enterprises";
        const pre = searchParams.get("product");
        if (pre) {
            const match = ALL_PRODUCT_NAMES.find((p) => p.toLowerCase().startsWith(pre.toLowerCase()));
            setForm((f) => ({ ...f, product: match || "Other / Multiple Items" }));
        }
    }, [searchParams]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry sent. We'll get back to you shortly.");
            setForm({ name: "", phone: "", product: "", message: "" });
        } catch (err) {
            toast.error("Could not send your enquiry. Please try WhatsApp or call us.");
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls =
        "w-full border border-input bg-white px-4 py-3 text-sm text-ink placeholder:text-muted-foreground/60 focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors";

    return (
        <main data-testid="contact-page">
            {/* HEADER */}
            <section className="pt-32 sm:pt-40 pb-14 sm:pb-20 blueprint-grid spotlight border-b border-border" data-testid="contact-header">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <MaskedLine delay={0.1}>
                        <span className="text-xs uppercase tracking-[0.3em] text-brand">Contact Us</span>
                    </MaskedLine>
                    <h1 className="font-display font-semibold uppercase tracking-tight text-5xl sm:text-7xl text-ink mt-3 leading-[0.95]">
                        <MaskedLine delay={0.25}>Let's talk</MaskedLine>
                        <MaskedLine delay={0.4}>
                            <span className="text-brand">bulk.</span>
                        </MaskedLine>
                    </h1>
                    <MaskedLine delay={0.55} className="mt-5">
                        <p className="text-muted-foreground max-w-xl text-base sm:text-lg leading-relaxed">
                            Tell us what you need and in what quantity. We respond with pricing and dispatch details — usually the same day.
                        </p>
                    </MaskedLine>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-testid="contact-body">
                {/* DETAILS */}
                <div className="lg:col-span-5 space-y-px">
                    <Reveal>
                        <div className="border border-border bg-white p-7 hover:border-brand transition-colors" data-testid="contact-card-email">
                            <div className="flex items-center gap-3 mb-3">
                                <Mail className="w-5 h-5 text-brand" />
                                <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Email</span>
                            </div>
                            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="font-display font-medium text-lg sm:text-xl text-ink hover:text-brand transition-colors break-all">
                                {CONTACT.email}
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="border border-border bg-white p-7 hover:border-brand transition-colors" data-testid="contact-card-phone">
                            <div className="flex items-center gap-3 mb-3">
                                <Phone className="w-5 h-5 text-brand" />
                                <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Mobile</span>
                            </div>
                            <a href={`tel:+91${CONTACT.phone}`} data-testid="contact-phone-link" className="font-display font-medium text-2xl sm:text-3xl text-ink hover:text-brand transition-colors tracking-wide">
                                +91 {CONTACT.phone}
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <div className="border border-border bg-white p-7 hover:border-brand transition-colors" data-testid="contact-card-location">
                            <div className="flex items-center gap-3 mb-3">
                                <MapPin className="w-5 h-5 text-brand" />
                                <span className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Location</span>
                            </div>
                            <span className="font-display font-medium text-lg sm:text-xl text-ink" data-testid="contact-location-text">{CONTACT.location}</span>
                            <p className="text-sm text-muted-foreground mt-2">Dispatching across India.</p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.24}>
                        <a
                            href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent("Hello Mamta Enterprises, I'd like to place a wholesale enquiry.")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-button"
                            className="group flex items-center justify-between bg-[#25D366] hover:bg-[#1eb856] text-white p-7 transition-colors"
                        >
                            <div>
                                <span className="text-[11px] uppercase tracking-[0.25em] text-white/80 block mb-1">Fastest response</span>
                                <span className="font-display font-medium text-xl uppercase tracking-wide">Chat on WhatsApp</span>
                            </div>
                            <MessageCircle className="w-8 h-8 transition-transform group-hover:scale-110" fill="currentColor" />
                        </a>
                    </Reveal>
                </div>

                {/* FORM */}
                <Reveal delay={0.1} className="lg:col-span-7">
                    <form onSubmit={submit} className="border border-border bg-white p-7 sm:p-10" data-testid="enquiry-form">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="font-display font-semibold uppercase tracking-tight text-2xl sm:text-3xl text-ink">Send an enquiry</h2>
                            <span className="hidden sm:block text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Wholesale only</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label htmlFor="enquiry-name" className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Your Name *</label>
                                <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="e.g. Rahul Sharma" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="enquiry-phone" className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Phone *</label>
                                <input id="enquiry-phone" data-testid="enquiry-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="Your mobile number" className={inputCls} />
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="enquiry-product" className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Product Interested In *</label>
                                <select id="enquiry-product" data-testid="enquiry-product-select" required value={form.product} onChange={set("product")} className={inputCls}>
                                    <option value="" disabled>Select a product</option>
                                    {ALL_PRODUCT_NAMES.map((p) => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="enquiry-message" className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Message</label>
                                <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Quantities, sizes, delivery location — anything that helps us quote faster." className={`${inputCls} resize-none`} />
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="enquiry-submit-button"
                            className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-display uppercase tracking-[0.15em] text-base px-8 py-4 transition-colors"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" /> Sending…
                                </>
                            ) : (
                                <>
                                    Submit Enquiry <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                </>
                            )}
                        </button>
                        <p className="text-xs text-muted-foreground mt-4 text-center">
                            Prefer talking? Call <a href={`tel:+91${CONTACT.phone}`} className="text-brand" data-testid="form-call-link">+91 {CONTACT.phone}</a> or message us on WhatsApp.
                        </p>
                    </form>
                </Reveal>
            </section>
        </main>
    );
};

export default Contact;
